import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarOff, HelpCircle, MicOff, Users } from 'lucide-react';
import ScorecardNavigation from './ScorecardNavigation';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import * as Sentry from '@sentry/react';
import {
  getWorkingTodayRoster,
  WorkingTodayResponse,
  RosterRow,
  LocationHealth,
} from '../../services/liveServersService';
import {
  sortRosterRows,
  matchStateChip,
  bannerCopyForStatus,
  deviceFaultBanners,
  rowDisplayName,
  formatClockTime,
  formatRosterDate,
  formatActivityAgo,
  formatUpdatedAgo,
  formatLastUpdated,
  ChipTone,
  FaultBanner,
  HEALTH_UNAVAILABLE_BANNER,
} from './liveServersRoster';
import {
  HEALTH_CHIP,
  HEALTH_CHIP_BASE,
  HEALTH_DOT,
  HEALTH_GLYPH,
  HEALTH_LABEL,
  LAST_SPEECH_COLUMN_HEADING,
  MIC_COLUMN_HEADING,
  SEVERITY_LEGEND,
  SEVERITY_LEGEND_LEAD,
  healthSubline,
  rowHealthState,
  rowStripeClass,
} from './deviceHealth';

const ROSTER_POLL_MS = 15_000; // PL decision #5 — poll the REST endpoint every 15s while mounted.

// Tailwind class maps for the status-chip tone. The danger/warning bindings are
// the feat-086 React bindings of the cross-stack error-*/warning-* families
// (tailwind.config.js + tokens.md) — never inline raw red-*/amber-* utilities.
const CHIP_CLASSES: Record<ChipTone, string> = {
  // feat-274 — colour only. The two pale pairs shipped here failed AA against
  // their own surfaces (success 2.538:1, warning 1.999:1) and this feature puts
  // a second chip family in the same row, so leaving them would render two
  // greens and two ambers a shade apart with no rule for which is which.
  // The inks are the ones feat-240 / feat-242 already established elsewhere.
  success: 'bg-success-light text-success-on',     // 4.623:1 (feat-240's ink)
  warning: 'bg-warning-surface text-attention-on', // 5.440:1 (feat-242's ink)
  danger: 'bg-danger-surface text-danger-text',    // 7.597:1 — unchanged
};
const DOT_CLASSES: Record<ChipTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning-solid',
  danger: 'bg-danger',
};
// The left-edge stripe moved to `deviceHealth.SEVERITY_STRIPE` (feat-274). It is
// now driven by the cross-axis severity ladder rather than by match state alone,
// and the old amber `#F59E0B` did not survive the move: it measures 2.148:1 on
// the card, under the 3:1 non-text floor. `attention.line #AE8213` replaces it.

const LiveServers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // feat-149 / ADR 0001: the roster is scoped to the tenant selected in the
  // top-left dropdown (`TenantContext.selectedTenant`). We PASS that id to the
  // backend, which is the sole authoritative scoper — we never filter tenants
  // client-side. Admin picks any tenant (dropdown visible; null until a first
  // pick). Corporate/Manager have `selectedTenant` auto-set to their own tenant
  // (dropdown hidden), so they always call and the backend returns only their
  // own tenant's rows (fail-closed; a non-pilot tenant like Grove → empty).
  const { selectedTenant } = useTenant();
  const rawRole = user?.managementUser?.role;
  const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
  const isAdmin = role === 'Admin';
  // Q1=(a): an admin who has not yet picked a tenant sees a "Select a tenant"
  // prompt and we DO NOT hit the endpoint. Corporate/Manager never reach this
  // (their tenant is auto-set), so it is admin-only by construction.
  const awaitingTenantSelection = isAdmin && selectedTenant === null;

  const [roster, setRoster] = useState<WorkingTodayResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Client epoch (ms) of the last successful roster fetch — drives "Updated Ns ago". */
  const [fetchedAtMs, setFetchedAtMs] = useState<number>(() => Date.now());

  // Selected location filter; null = "Both locations" (default).
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // 1s ticker so relative times + the "Updated Ns ago" line advance smoothly.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // ---------- Roster polling ----------

  const loadRoster = useCallback(async () => {
    // Q1=(a): admin with no tenant selected — never call the endpoint; the
    // render below shows the "Select a tenant" prompt instead.
    if (isAdmin && selectedTenant === null) {
      if (!mountedRef.current) return;
      setRoster(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    try {
      // Pass the selected tenant (undefined when unset for corp/manager before
      // auto-set — the backend then scopes off the token). Backend is the sole
      // authoritative scoper; we never tenant-filter the returned rows.
      const data = await getWorkingTodayRoster(selectedTenant ?? undefined);
      if (!mountedRef.current) return;
      setRoster(data);
      setFetchedAtMs(Date.now());
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('[LiveServers] Failed to load roster:', err);
      Sentry.captureException(err);
      if (!mountedRef.current) return;
      // Keep the last good roster on a transient poll failure; only surface the
      // error screen if we have nothing to show yet.
      setIsLoading(false);
      setRoster((prev) => {
        if (!prev) setError('Failed to load the working-today roster.');
        return prev;
      });
    }
  }, [isAdmin, selectedTenant]);

  // When the tenant selection changes, drop the previous tenant's roster
  // immediately so we never show tenant A's rows (or its location toggle)
  // while tenant B is loading, and reset the per-location filter (locations
  // are tenant-specific). The poll effect below re-fetches right after.
  useEffect(() => {
    setRoster(null);
    setSelectedLocationId(null);
    setIsLoading(true);
  }, [selectedTenant]);

  useEffect(() => {
    mountedRef.current = true;
    // loadRoster is re-created when selectedTenant (or role) changes, so this
    // effect re-runs on a dropdown switch: it clears the old interval and calls
    // loadRoster immediately — re-scoping within the same tick, not on the next
    // 15s poll — then re-arms the 15s poll for the newly selected tenant.
    loadRoster();
    pollTimerRef.current = setInterval(loadRoster, ROSTER_POLL_MS);
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [loadRoster]);

  // ---------- 1-second ticker ----------

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // feat-274: the /ws/admin socket that used to drive this page is GONE.
  //
  // Its only consumer was the "Recording now" cell, which rendered a green
  // pulsing dot off membership of `active_servers`. That membership is socket
  // liveness, not pipeline liveness: it stayed true for all 23h 31m of the
  // incident while the microphone was dead, and a manager reads that cell as the
  // microphone. The Mic column below reports `device_health.state` instead —
  // classified server-side, off `bytes_sent` movement. Nothing about how
  // `active_servers` is computed or delivered changed; this page just stopped
  // reporting a measurement it did not have. See QUESTIONS-FRONTEND.md Q2.

  // ---------- Derived view state ----------

  const asOfMs = useMemo(() => {
    const t = roster ? Date.parse(roster.as_of) : NaN;
    return Number.isNaN(t) ? Date.now() : t;
  }, [roster]);

  // De-skewed, smoothly-advancing server "now": as_of + client elapsed since fetch.
  const serverNowMs = asOfMs + (nowTick - fetchedAtMs);

  const locations = useMemo<LocationHealth[]>(
    () => roster?.per_location_health ?? [],
    [roster],
  );

  const visibleRows = useMemo<RosterRow[]>(() => {
    if (!roster) return [];
    const filtered = selectedLocationId
      ? roster.rows.filter((r) => r.location_id === selectedLocationId)
      : roster.rows;
    return sortRosterRows(filtered);
  }, [roster, selectedLocationId]);

  // Fault banners are derived from the VISIBLE rows, never from a separate
  // query — a banner that could disagree with the table under it is worse than
  // no banner. A healthy roster produces none, which is what makes a banner's
  // presence meaningful.
  const faultBanners = useMemo<FaultBanner[]>(
    () => deviceFaultBanners(visibleRows, serverNowMs),
    [visibleRows, serverNowMs],
  );

  // AC7 / Panel F. Suppressed on an empty roster: there are no rows to be
  // unknown about, and the page is already saying nobody is clocked in.
  const healthUnavailable = !!roster && !roster.device_health_available && visibleRows.length > 0;

  const visibleBanners = useMemo<LocationHealth[]>(
    () =>
      locations
        .filter((l) => l.status !== 'ok')
        .filter((l) => !selectedLocationId || l.location_id === selectedLocationId),
    [locations, selectedLocationId],
  );

  // ---------- Loading / error gates ----------

  // Q1=(a): admin who hasn't picked a tenant yet. Highest-precedence gate — we
  // never called the endpoint, so show the "Select a tenant" prompt (not a
  // spinner, not the empty roster). Corporate/Manager never hit this branch.
  if (awaitingTenantSelection) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <div className="mb-5">
            <h1 className="font-display font-semibold text-[2rem] leading-tight text-ink">Live Servers</h1>
            <p className="mt-1 text-sm text-inkMuted">Who's on the clock today across Square &amp; Bespoke</p>
          </div>
          <SelectTenantPrompt />
        </div>
      </div>
    );
  }

  if (isLoading && !roster) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner message="Loading today's roster…" />
        </div>
      </div>
    );
  }

  if (error && !roster) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <ErrorMessage
            title="Couldn't load the roster"
            // Third sentence is load-bearing: a manager who has been told to
            // watch this page instead of Sentry will otherwise read a failed
            // load as a clean bill of health.
            message={
              'The request to /api/admin/live-servers/working-today failed. ' +
              'This is not a report that everything is fine — microphone health ' +
              'is unknown for every server until this loads.'
            }
            details={error}
            onRetry={() => {
              setError(null);
              setIsLoading(true);
              loadRoster();
            }}
            onGoBack={() => navigate('/restaurant/leaderboard')}
          />
        </div>
      </div>
    );
  }

  // ---------- Main render ----------

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="font-display font-semibold text-[2rem] leading-tight text-ink">Live Servers</h1>
            <p className="mt-1 text-sm text-inkMuted">
              Who's on the clock today across Square &amp; Bespoke
              {roster && formatRosterDate(roster.as_of) ? ` — ${formatRosterDate(roster.as_of)}` : ''}
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* Location filter — default "Both locations" */}
            <div className="inline-flex rounded-lg border border-softBorder bg-white p-0.5 text-sm font-semibold">
              <button
                onClick={() => setSelectedLocationId(null)}
                className={`px-3 py-1.5 rounded-md ${
                  selectedLocationId === null ? 'bg-coral-light text-coral' : 'text-inkMuted hover:bg-cream-dark'
                }`}
              >
                Both locations
              </button>
              {locations.map((loc) => (
                <button
                  key={loc.location_id}
                  onClick={() => setSelectedLocationId(loc.location_id)}
                  className={`px-3 py-1.5 rounded-md ${
                    selectedLocationId === loc.location_id
                      ? 'bg-coral-light text-coral'
                      : 'text-inkMuted hover:bg-cream-dark'
                  }`}
                >
                  {loc.location_name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 text-xs text-inkMuted">
              <span>Updated {formatUpdatedAgo(nowTick - fetchedAtMs)}</span>
            </div>
          </div>
        </div>

        {/* Device-health fault banners. Red and amber are NEVER collapsed into
            one — they carry different actions and different urgency, and both
            may render at once. */}
        {faultBanners.map((b) => (
          <FaultBannerRow key={b.key} banner={b} />
        ))}
        {healthUnavailable && <FaultBannerRow banner={HEALTH_UNAVAILABLE_BANNER} />}

        {/* Per-location degraded banners (render independently) */}
        {visibleBanners.map((loc) => (
          <DegradedBanner
            key={loc.location_id}
            location={loc}
            lastPollAgo={
              loc.status === 'stale_poll' && loc.last_poll_at
                ? formatActivityAgo(loc.last_poll_at, serverNowMs)
                : null
            }
          />
        ))}

        {/* Legend / band key — only when there are rows to key */}
        {visibleRows.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-inkMuted">
            <span className="font-bold text-ink">{SEVERITY_LEGEND_LEAD}</span>
            {SEVERITY_LEGEND.map((entry) => (
              <LegendDot key={entry.label} className={entry.className} label={entry.label} />
            ))}
          </div>
        )}

        {/* Roster table or empty state */}
        {visibleRows.length > 0 ? (
          <div className="bg-white rounded-2xl border border-softBorder shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-[1080px] w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-inkMuted bg-cream/60 border-b border-softBorder">
                    <th className="px-4 py-3 w-[200px]">Status</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Square clock-in</th>
                    <th className="px-4 py-3">Square clock-out</th>
                    <th className="px-4 py-3">Bespoke shift start</th>
                    <th className="px-4 py-3 w-[220px]">{MIC_COLUMN_HEADING}</th>
                    <th className="px-4 py-3">{LAST_SPEECH_COLUMN_HEADING}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-softBorder">
                  {visibleRows.map((row, i) => (
                    <RosterTableRow
                      key={`${row.match_state}-${row.server_id ?? row.vendor_employee_id ?? i}`}
                      row={row}
                      serverNowMs={serverNowMs}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyRoster />
        )}

        {visibleRows.length > 0 && (
          <div className="mt-2 flex items-start justify-between gap-4 text-xs text-inkMuted">
            <p>
              Faults and mismatches stay at the top even when you filter by a single location. Everything
              on this page refreshes every 15s.
            </p>
            <span className="shrink-0 text-right">Last updated {formatLastUpdated(fetchedAtMs)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Sub-components ----------

const LegendDot: React.FC<{ className: string; label: string }> = ({ className, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={`w-2.5 h-2.5 rounded-sm ${className}`} />
    {label}
  </span>
);

/**
 * Device-health fault banner. Extends feat-086's amber-only `DegradedBanner`
 * with a danger variant and a neutral one — same geometry, same icon placement,
 * same copy formula (bold lede + one actionable sentence).
 *
 * Class strings are whole literals per tone, never interpolated: Tailwind purges
 * classes it cannot see as whole tokens, and a purged class fails SILENTLY as an
 * unstyled render — which on a fault banner means an alarm that does not look
 * like one.
 */
const FaultBannerRow: React.FC<{ banner: FaultBanner }> = ({ banner }) => {
  const shell =
    banner.tone === 'danger'
      ? 'mb-3 flex items-start gap-3 rounded-xl border border-danger-line/40 bg-danger-surface px-4 py-3'
      : banner.tone === 'attention'
      ? 'mb-3 flex items-start gap-3 rounded-xl border border-attention-line/50 bg-warning-surface px-4 py-3'
      : 'mb-3 flex items-start gap-3 rounded-xl border border-inkMuted/30 bg-white px-4 py-3';
  const ledeCls =
    banner.tone === 'danger'
      ? 'font-bold text-danger-text'
      : banner.tone === 'attention'
      ? 'font-bold text-attention-on'
      : 'font-bold text-ink';
  const detailCls = banner.tone === 'neutral' ? 'text-inkMuted mt-0.5' : 'text-ink mt-0.5';
  const Icon = banner.tone === 'neutral' ? HelpCircle : AlertTriangle;
  const iconCls =
    banner.tone === 'danger'
      ? 'w-5 h-5 mt-0.5 shrink-0 text-danger'
      : banner.tone === 'attention'
      ? 'w-5 h-5 mt-0.5 shrink-0 text-attention-on'
      : 'w-5 h-5 mt-0.5 shrink-0 text-inkMuted';

  return (
    <div className={shell}>
      <Icon className={iconCls} aria-hidden="true" />
      <div className="text-sm">
        <div className={ledeCls}>{banner.lede}</div>
        <div className={detailCls}>{banner.detail}</div>
      </div>
    </div>
  );
};

/**
 * The Mic cell — feat-274's whole point.
 *
 * It renders `device_health.state` and NOTHING ELSE. It does not look at a
 * timestamp to decide what to show; the durations under the chip explain a state
 * the backend already classified. A row with no `device_health` renders
 * `unknown`, never healthy and never blank — a blank cell in a column of green
 * chips reads as "fine", which is the bug this feature exists to fix.
 *
 * The cell never truncates and never wraps: `whitespace-nowrap` on both the chip
 * and the sub-line, `w-[220px]` on the header cell.
 */
const MicCell: React.FC<{ row: RosterRow; serverNowMs: number }> = ({ row, serverNowMs }) => {
  const state = rowHealthState(row);
  const glyph = HEALTH_GLYPH[state];
  const sub = row.device_health ? healthSubline(row.device_health, serverNowMs) : null;

  return (
    <td className="px-4 py-3">
      <span className={`${HEALTH_CHIP_BASE} ${HEALTH_CHIP[state]}`}>
        {glyph === 'mic-off' ? (
          <MicOff className="w-3 h-3 shrink-0" aria-hidden="true" />
        ) : glyph === 'help' ? (
          <HelpCircle className="w-3 h-3 shrink-0" aria-hidden="true" />
        ) : (
          <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[state]}`} />
        )}
        {HEALTH_LABEL[state]}
      </span>
      {sub && <div className={`mt-1 ${sub.cls}`}>{sub.text}</div>}
    </td>
  );
};

const DegradedBanner: React.FC<{ location: LocationHealth; lastPollAgo: string | null }> = ({
  location,
  lastPollAgo,
}) => {
  const copy = bannerCopyForStatus(location.status, location.location_name, lastPollAgo);
  if (!copy) return null;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-warning-solid/40 bg-warning-surface px-4 py-3">
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-warning-on" />
      <div className="text-sm leading-snug">
        <span className="font-bold text-ink">{copy.headline}</span>
        <span className="text-ink/80"> {copy.detail}</span>
        <span className="block mt-0.5 text-xs text-inkMuted">{copy.note}</span>
      </div>
    </div>
  );
};

const StatusChip: React.FC<{ row: RosterRow }> = ({ row }) => {
  const chip = matchStateChip(row.match_state);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${CHIP_CLASSES[chip.tone]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${DOT_CLASSES[chip.tone]}`} />
      {chip.label}
    </span>
  );
};

const RosterTableRow: React.FC<{ row: RosterRow; serverNowMs: number }> = ({
  row,
  serverNowMs,
}) => {
  const chip = matchStateChip(row.match_state);
  const name = rowDisplayName(row);
  const hasSquare = row.square_first_clock_in_at !== null;

  // The stripe means "the most urgent thing about this row", across BOTH axes.
  // A green "Both" chip beside a red stripe is correct and expected: Status
  // answers "is this person reconciled between Square and Bespoke", Mic answers
  // "is their microphone working". The pre-existing failure was that nothing on
  // the row answered the second question, so the first one got read as if it had.
  return (
    <tr className={`${rowStripeClass(row)} hover:bg-cream/50`}>
      {/* Status */}
      <td className="px-4 py-3">
        <StatusChip row={row} />
      </td>

      {/* Name (+ unmapped sub-badge) */}
      <td className="px-4 py-3 max-w-[220px]">
        <div className="font-bold text-ink truncate" title={name}>
          {name}
        </div>
        {chip.unmapped && (
          <span className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger-em text-danger-text">
            Unmapped Team Member
          </span>
        )}
      </td>

      {/* Location */}
      <td className="px-4 py-3 text-inkMuted whitespace-nowrap">{row.location_name}</td>

      {/* Square clock-in */}
      <td className="px-4 py-3 text-ink whitespace-nowrap">
        {hasSquare ? (
          <>
            {formatClockTime(row.square_first_clock_in_at)}
            {row.square_currently_clocked_in && (
              <span className="ml-1 inline-flex items-center gap-1 align-middle text-[10px] font-bold text-success">
                <span className="w-1.5 h-1.5 rounded-full bg-success" />
                live
              </span>
            )}
            {row.square_shift_count_today >= 2 && (
              <span
                className="ml-1 align-middle text-[10px] font-bold text-inkMuted underline decoration-dotted cursor-help"
                title={`${row.square_shift_count_today} shifts today`}
              >
                ·{row.square_shift_count_today} shifts
              </span>
            )}
          </>
        ) : (
          <span className="text-inkMuted">—</span>
        )}
      </td>

      {/* Square clock-out */}
      <td className="px-4 py-3 text-ink whitespace-nowrap">
        {!hasSquare ? (
          <span className="text-inkMuted">—</span>
        ) : row.square_currently_clocked_in ? (
          <span className="text-inkMuted">Currently clocked in</span>
        ) : (
          formatClockTime(row.square_latest_clock_out_at)
        )}
      </td>

      {/* Bespoke shift start */}
      <td className="px-4 py-3 text-ink whitespace-nowrap">
        {row.bespoke_first_shift_start_at ? (
          formatClockTime(row.bespoke_first_shift_start_at)
        ) : (
          <span className="text-inkMuted">—</span>
        )}
      </td>

      {/* Mic — server-classified device health (feat-274) */}
      <MicCell row={row} serverNowMs={serverNowMs} />

      {/* Last speech — MAX(transcript_part.created). SPEECH, not audio. */}
      <td className="px-4 py-3 text-inkMuted whitespace-nowrap">
        {formatActivityAgo(row.last_activity_at, serverNowMs)}
      </td>
    </tr>
  );
};

/**
 * Panel D. "Checked just now" is load-bearing copy, not decoration: per the
 * feat-199 triad, empty asserts a MEASUREMENT — the roster looked and found
 * nobody — while error asserts IGNORANCE. On this surface a manager reads an
 * empty table as "no problems", so it has to be unmistakable that the table
 * looked. It differs from the error panel on icon, icon colour, heading colour,
 * copy and the presence of an action, so the two are not told apart by hue.
 */
const EmptyRoster: React.FC = () => (
  <div className="bg-white rounded-2xl border border-softBorder shadow p-8 text-center">
    <div className="mx-auto w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mb-3">
      <CalendarOff className="w-6 h-6 text-inkMuted" />
    </div>
    <h3 className="font-display font-semibold text-ink">Nobody is clocked in today</h3>
    <p className="mt-1 text-sm text-inkMuted">
      Checked just now against Square and Bespoke. Microphone health appears here as soon as a
      shift starts.
    </p>
  </div>
);

// feat-149 Q1=(a): shown to an admin who hasn't selected a tenant yet. Copy-only
// prompt (no design-lead) pointing at the top-left tenant dropdown.
const SelectTenantPrompt: React.FC = () => (
  <div className="bg-white rounded-2xl border border-softBorder shadow p-8 text-center">
    <div className="mx-auto w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mb-3">
      <Users className="w-6 h-6 text-inkMuted" />
    </div>
    <h3 className="font-display font-semibold text-ink">Select a tenant</h3>
    <p className="mt-1 text-sm text-inkMuted">
      Choose a tenant from the menu in the top-left to see who's on the clock today.
    </p>
  </div>
);

export default LiveServers;
